import * as UpsellsLib from '@sumup/hotpink-upsells';

import { Product, UpsellV2Channel, UpsellV2Point } from '@ordering/gql/graphql';
import { OrderLine } from '@ordering/types/Order';
import { ProductUpsellItem, ProductUpsellResult } from '@ordering/types/Upsells';
import type { Inventory } from '@ordering/providers/InventoryProvider/inventory.utils';

import type { UpsellTypes } from './upsells.d';
import { variantIdsMatch } from './variantId';

const KmpUpsells = UpsellsLib.com.hotpink.upsells;

export const KmpUpsellsModel = KmpUpsells.models;
export const KmpUpsellsFactories = KmpUpsells.factories;
export const kmpUpsellsEngine = new KmpUpsells.Upsells();

type KmpUpsell = UpsellsLib.com.hotpink.upsells.models.Upsell;
type KmpSuggestion = UpsellsLib.com.hotpink.upsells.models.Suggestion;
type KmpUpsellPoint = UpsellsLib.com.hotpink.upsells.models.UpsellPoint;
type KmpInventory = UpsellsLib.com.hotpink.upsells.models.Inventory;
type KmpBasketItem = UpsellsLib.com.hotpink.upsells.models.BasketItem;

const isCategoryTarget = (target: UpsellTypes.Target): target is UpsellTypes.CategoryTarget => 'categoryId' in target;

const isItemSuggestion = (suggestion: UpsellTypes.Suggestion): suggestion is UpsellTypes.ItemSuggestion =>
  'variantId' in suggestion;

const menuPoints = [UpsellV2Point.Menu, UpsellV2Point.OnOrderStart];
const checkoutPoints = [UpsellV2Point.Checkout, UpsellV2Point.OnCheckout];
const variantPoints = [UpsellV2Point.Variant, UpsellV2Point.OnItemAdded];

const mapRawProductToKmpUpsellsProduct = (rawProduct: Product): UpsellsLib.com.hotpink.upsells.models.Product => {
  const categories = rawProduct.category ? [KmpUpsellsModel.createCategory(rawProduct.category.id)] : [];
  const variants = rawProduct.variants.map((variant) =>
    KmpUpsellsModel.createVariant(variant.id, mapInventoryComponents(variant.inventoryComponents))
  );
  return KmpUpsellsModel.createProduct(rawProduct.id, variants, categories);
};

const mapRawProductsToKmpUpsellsMenu = (rawProducts: Product[]): UpsellsLib.com.hotpink.upsells.models.Menu =>
  KmpUpsellsModel.createMenu(rawProducts.map((rawProduct) => mapRawProductToKmpUpsellsProduct(rawProduct)));

const mapInventoryComponents = (components: { componentId: string; quantifier: number }[] = []) =>
  components.map((component) => KmpUpsellsModel.createInventoryComponent(component.componentId, component.quantifier));

export const mapInventoryToKmp = (inventory?: Inventory | null): KmpInventory | null => {
  if (!inventory) return null;

  return KmpUpsellsModel.createInventory(
    Object.entries(inventory).map(([inventoryId, inventoryOnHand]) =>
      KmpUpsellsModel.createInventoryUnit(inventoryId, inventoryOnHand)
    )
  );
};

export const mapBasketToKmp = (basket: OrderLine[] = []): KmpBasketItem[] =>
  basket.map((line) =>
    KmpUpsellsModel.createBasketItem(
      KmpUpsellsModel.createVariant(line.variant.id, mapInventoryComponents(line.variant.inventoryComponents)),
      line.quantity
    )
  );

const mapCampaignSuggestions = (campaign: UpsellTypes.Upsell) =>
  campaign.upsells
    .filter(isItemSuggestion)
    .map((suggestion) =>
      KmpUpsellsFactories.upsellSuggestion(suggestion.productId, suggestion.variantId, suggestion.sortIndex)
    );

export const mapRawUpsellsToKmpUpsells = (rawUpsells: UpsellTypes.Upsell[]): KmpUpsell[] => {
  const kmpUpsells: KmpUpsell[] = [];

  for (const campaign of rawUpsells) {
    if (!campaign.channels.includes(UpsellV2Channel.Ordering)) {
      continue;
    }

    const suggestions = mapCampaignSuggestions(campaign);

    if (suggestions.length === 0) {
      continue;
    }

    const point = KmpUpsellsModel.UpsellPoint.valueOf(campaign.point);
    const style = KmpUpsellsModel.UpsellStyle.valueOf(campaign.style);
    const type = KmpUpsellsModel.UpsellType.valueOf(campaign.type);

    const targets = campaign.targets.map((target) =>
      isCategoryTarget(target)
        ? KmpUpsellsFactories.upsellCategoryTarget(target.categoryId, target.categoryName)
        : KmpUpsellsFactories.upsellVariantTarget(target.productId, target.variantIds)
    );

    // Menu/checkout = untargeted (no selected product). 
    // Variant = targeted when targets exist.
    const useTargeted = targets.length > 0 && variantPoints.includes(campaign.point);

    if (useTargeted) {
      kmpUpsells.push(
        KmpUpsellsFactories.targetedUpsell(
          campaign.id,
          type,
          style,
          campaign.name,
          campaign.headline,
          targets,
          suggestions,
          point
        )
      );
    } else {
      kmpUpsells.push(
        KmpUpsellsFactories.untargetedUpsell(
          campaign.id,
          type,
          point,
          style,
          campaign.name,
          campaign.headline,
          suggestions
        )
      );
    }
  }

  return kmpUpsells;
};

export type PrecomputedUpsells = {
  menu: UpsellsLib.com.hotpink.upsells.models.Menu;
  kmpUpsells: KmpUpsell[];
  productsById: Map<string, Product>;
  rawUpsells: UpsellTypes.Upsell[];
  inventory: KmpInventory | null;
};

export const precomputeUpsells = (
  allProducts: Product[],
  rawUpsells: UpsellTypes.Upsell[],
  inventory?: Inventory | null
): PrecomputedUpsells => ({
  menu: mapRawProductsToKmpUpsellsMenu(allProducts),
  kmpUpsells: mapRawUpsellsToKmpUpsells(rawUpsells),
  productsById: new Map(allProducts.map((product) => [product.id, product])),
  rawUpsells,
  inventory: mapInventoryToKmp(inventory),
});

type RawSuggestion = {
  productId: string;
  variantId?: string;
  upsellId: string;
};

const toUpsellItems = (suggestions: RawSuggestion[], productsById: Map<string, Product>): ProductUpsellItem[] =>
  suggestions.flatMap((suggestion) => {
    const product = productsById.get(suggestion.productId);

    if (!product) {
      return [];
    }

    const { variantId } = suggestion;
    const matchingVariant = variantId
      ? product.variants.find((variant) => variantIdsMatch(variant.id, variantId))
      : undefined;

    return [{ product, variantId: matchingVariant?.id ?? variantId, upsellId: suggestion.upsellId }];
  });

const toProductUpsellResults = (
  kmpSuggestions: KmpSuggestion[],
  rawUpsells: UpsellTypes.Upsell[],
  productsById: Map<string, Product>
): ProductUpsellResult[] => {
  const productIdsByCampaign = new Map<string, [string, string][]>();

  for (const kmpSuggestion of kmpSuggestions) {
    const productIds = productIdsByCampaign.get(kmpSuggestion.upsellId) ?? [];
    productIds.push([kmpSuggestion.productId, kmpSuggestion.variant.id]);
    productIdsByCampaign.set(kmpSuggestion.upsellId, productIds);
  }

  return Array.from(productIdsByCampaign.entries()).flatMap(([upsellId, productIds]) => {
    const campaign = rawUpsells.find((rawUpsell) => rawUpsell.id === upsellId);

    if (!campaign) {
      return [];
    }

    const suggestions = productIds.map(([productId, variantId]) => ({
      productId,
      variantId,
      upsellId,
    }));

    return [
      {
        upsellId,
        headline: campaign.headline,
        style: campaign.style,
        items: toUpsellItems(suggestions, productsById),
      },
    ];
  });
};

const toKmpPoints = (points: UpsellV2Point[]): KmpUpsellPoint[] =>
  points.map((point) => KmpUpsellsModel.UpsellPoint.valueOf(point));

export const getProductUpsellItems = (
  selectedProduct: Product,
  precomputed: PrecomputedUpsells,
  orderLines: OrderLine[] = []
): ProductUpsellResult[] => {
  const { menu, kmpUpsells, productsById, rawUpsells, inventory } = precomputed;
  const basket = mapBasketToKmp(orderLines);
  const selectedKmpProduct = mapRawProductToKmpUpsellsProduct(selectedProduct);

  const kmpSuggestions = toKmpPoints(variantPoints).flatMap((point) => [
    ...kmpUpsellsEngine.getTargetedUpsells(selectedKmpProduct, menu, kmpUpsells, point, undefined, inventory, basket),
    ...kmpUpsellsEngine.getUntargetedUpsells(menu, kmpUpsells, point, undefined, inventory, basket),
  ]);

  return toProductUpsellResults(kmpSuggestions, rawUpsells, productsById);
};

const getUntargetedUpsellItemsForPoints = (
  precomputed: PrecomputedUpsells,
  points: UpsellV2Point[],
  orderLines: OrderLine[] = []
): ProductUpsellResult[] => {
  const { menu, kmpUpsells, productsById, rawUpsells, inventory } = precomputed;
  const basket = mapBasketToKmp(orderLines);

  const kmpSuggestions = toKmpPoints(points).flatMap((point) =>
    kmpUpsellsEngine.getUntargetedUpsells(menu, kmpUpsells, point, undefined, inventory, basket)
  );

  return toProductUpsellResults(kmpSuggestions, rawUpsells, productsById);
};

export const getCheckoutUpsellItems = (precomputed: PrecomputedUpsells, orderLines: OrderLine[] = []) =>
  getUntargetedUpsellItemsForPoints(precomputed, checkoutPoints, orderLines);

export const getMenuUpsellItems = (precomputed: PrecomputedUpsells, orderLines: OrderLine[] = []) =>
  getUntargetedUpsellItemsForPoints(precomputed, menuPoints, orderLines);

export { UpsellTypes };
